// A2F-615: Permanent public install instructions page.
//
// For authenticated users who arrive here post-email-confirmation, the
// legacy post-signup hydration (pending-profile promotion, trial start)
// still runs silently in the background and their ribbon preview is shown.
// Anonymous users see the full install instructions with no redirect.

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Loader2,
  MonitorCheck,
  Package,
  ShieldCheck,
  WifiOff,
} from 'lucide-react'
import { useAuth } from '../lib/AuthContext'
import * as api from '../lib/api'
import type { ProfileResponse } from '../lib/api'
import { Telemetry } from '../lib/telemetry'
import type { TierChoice } from '../lib/telemetry-types'
import {
  clearPendingMetadata,
  clearPendingProfile,
  clearPendingTier,
  readPendingSignup,
} from '../components/SignupModal'
import { Nav } from '../components/Nav'
import { TermsPrivacyFooter } from '../components/TermsPrivacyFooter'

const APPSOURCE_URL = 'https://appsource.microsoft.com/en-us/product/office/WA200010829'

export function InstallPage() {
  const { session, loading: authLoading } = useAuth()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [hydrating, setHydrating] = useState(false)
  const [trialError, setTrialError] = useState<string | null>(null)
  const [retryingTrial, setRetryingTrial] = useState(false)
  const [showManual, setShowManual] = useState(false)
  const [showTroubleshooting, setShowTroubleshooting] = useState(false)
  // Batch 4.5: corr_B — post-confirm half of the Option C correlation handoff.
  const [correlationId] = useState<string>(() => crypto.randomUUID())
  // A2F-913: snapshot the FRE-completed flag once on mount and clear
  // it immediately so a subsequent direct-nav visit to /install gets
  // the normal banner. Suppresses only the very next render pass.
  const [fromFreSignup] = useState<boolean>(() => {
    try {
      const flag = localStorage.getItem('chrona-pro:fre-completed')
      if (flag) localStorage.removeItem('chrona-pro:fre-completed')
      return flag === '1'
    } catch {
      return false
    }
  })

  const runStartTrial = useCallback(
    async (current: ProfileResponse): Promise<ProfileResponse> => {
      try {
        const sub = await api.startTrial()
        clearPendingTier()
        setTrialError(null)
        return { ...current, subscription: sub }
      } catch (e) {
        console.warn('startTrial failed', e)
        setTrialError(
          "We couldn't start your trial. You can retry below, or continue on the free tier.",
        )
        return current
      }
    },
    [],
  )

  const retryTrial = useCallback(async () => {
    if (!profile) return
    setRetryingTrial(true)
    try {
      const next = await runStartTrial(profile)
      setProfile(next)
    } finally {
      setRetryingTrial(false)
    }
  }, [profile, runStartTrial])

  const dismissTrialError = useCallback(() => {
    clearPendingTier()
    setTrialError(null)
  }, [])

  // Post-signup hydration — only runs for authenticated users. Promotes
  // pending profile, starts trial, fires telemetry. Anonymous visitors
  // skip this entirely and see the static install instructions below.
  useEffect(() => {
    if (authLoading || !session) return

    let cancelled = false
    setHydrating(true)
    ;(async () => {
      try {
        const pending = readPendingSignup()
        let current = await api.getProfile({ correlationId })

        let promotedOk = false
        if (!pending.profile && current.profileId === null) {
          const promoted = await api.promotePendingProfile({ correlationId })
          if (promoted) {
            current = promoted.profile
            promotedOk = true
          }
        }

        Telemetry.onboardingInstallPageViewed({
          correlation_id: correlationId,
          has_pending_result: promotedOk,
        })

        if (pending.profile) {
          current = await api.saveProfile(
            pending.profile.domain || 'biotech_ectd',
            pending.profile as import('../types').Profile,
            { correlationId },
          )
          clearPendingProfile()
        }

        if (pending.tier === 'trial' || pending.tier === 'subscribe') {
          current = await runStartTrial(current)
        } else if (pending.tier === 'free') {
          clearPendingTier()
        }

        if (pending.tier) {
          Telemetry.configuratorSignupCompleted({
            tier_choice: pending.tier as TierChoice,
          })
        }

        if (pending.metadata) {
          const props: Record<string, unknown> = {}
          if (pending.metadata.displayName) props.display_name = pending.metadata.displayName
          if (pending.metadata.organization) props.organization = pending.metadata.organization
          if (pending.metadata.roleTitle) props.role_title = pending.metadata.roleTitle
          if (Object.keys(props).length > 0) Telemetry.setPersonProperties(props)
          clearPendingMetadata()
        }

        if (!cancelled) setProfile(current)
      } catch (e) {
        console.error('install hydration failed', e)
      } finally {
        if (!cancelled) setHydrating(false)
      }
    })()

    return () => { cancelled = true }
  }, [session, authLoading, runStartTrial, correlationId])

  const manifestHref =
    profile?.manifestToken && profile.manifestVersion > 0
      ? api.manifestUrl(profile.manifestToken, profile.manifestVersion)
      : null
  const msWordUrl = manifestHref ? `ms-word:ofe|u|${manifestHref}` : null

  return (
    <div className="min-h-screen bg-brand-bg text-brand-charcoal font-sans">
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-10">

        {/* ── Post-signup context for authenticated users ── */}
        {session && hydrating && (
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-4">
            <Loader2 className="animate-spin" size={16} />
            Setting up your account…
          </div>
        )}

        {session && !hydrating && profile && !profile.profileId && !fromFreSignup && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 text-center space-y-3">
            <p className="text-sm text-slate-600">
              Your ribbon is ready to configure. Pick your rules before installing.
            </p>
            <Link
              to="/configure"
              className="inline-block rounded-md bg-brand-orange text-white px-5 py-2.5 text-sm font-medium hover:bg-brand-orange/90"
            >
              Build my ribbon
            </Link>
          </section>
        )}

        {trialError && (
          <section className="max-w-lg mx-auto border border-amber-300 bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-900 flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div className="flex-1 space-y-2">
              <p>{trialError}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={retryTrial}
                  disabled={retryingTrial}
                  className="rounded-md bg-amber-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-amber-700 disabled:opacity-60"
                >
                  {retryingTrial ? 'Retrying…' : 'Retry trial start'}
                </button>
                <button
                  type="button"
                  onClick={dismissTrialError}
                  className="rounded-md bg-transparent text-amber-900 px-3 py-1.5 text-xs font-medium hover:bg-amber-100"
                >
                  Stay on free tier
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ── Primary install CTA — Chrona Pro ── */}
        <section className="space-y-4">
          <h1 className="text-2xl font-bold tracking-tight">Install Chrona Pro in Word</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Chrona Pro is a Microsoft Word Add-in. Install it from AppSource or follow the
            steps below to add it to your ribbon.
          </p>
          <a
            href={APPSOURCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => Telemetry.configuratorInstallAttempted({ method: 'one_click' })}
            className="inline-flex items-center gap-2 rounded-md bg-brand-orange text-white px-6 py-3 text-sm font-medium hover:bg-brand-orange/90"
          >
            Install from Microsoft AppSource
            <ExternalLink size={14} />
          </a>
          <p className="text-xs text-slate-400">
            Works best on Windows desktop Word (Word 2016 or later, or Microsoft 365).
          </p>
        </section>

        {/* ── Post-install steps ── */}
        <section className="space-y-4">
          <h2 className="text-base font-semibold">After installation</h2>
          <ol className="space-y-4">
            {[
              { icon: <MonitorCheck size={18} />, text: 'Open any Word document.' },
              { icon: <Package size={18} />, text: 'Look for the Chrona tab in the ribbon at the top of the window.' },
              { icon: <ShieldCheck size={18} />, text: 'Click any Chrona button to open the taskpane.' },
              {
                icon: <ExternalLink size={18} />,
                text: session
                  ? "You're already signed in. Your ribbon is ready to use."
                  : 'Sign in with the email you used on this site.',
              },
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="shrink-0 h-8 w-8 rounded-full bg-brand-orange/10 text-brand-orange grid place-items-center">
                  {step.icon}
                </span>
                <p className="text-sm text-slate-700 leading-relaxed pt-1">{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Chrona Enterprise ── */}
        <section className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-700 space-y-1">
          <p className="font-medium text-brand-charcoal">Chrona Enterprise</p>
          <p className="text-slate-500">
            Enterprise teams have Chrona deployed centrally via M365 Centralized Deployment by
            their IT admin — no individual install required. Look for the{' '}
            <strong>Chrona</strong> tab in your Word ribbon.{' '}
            <a
              href="mailto:contact@chronabio.ai?subject=Chrona%20Enterprise"
              className="text-brand-orange hover:underline"
            >
              Contact us
            </a>{' '}
            to learn more.
          </p>
        </section>

        {/* ── Manual sideload (collapsible) ── */}
        <section className="border border-slate-200 bg-white rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowManual((s) => !s)}
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium hover:bg-slate-50 text-left"
          >
            <span>Manual install (for IT-restricted environments)</span>
            {showManual ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showManual && (
            <div className="px-5 pb-5 pt-1 border-t border-slate-100">
              {manifestHref ? (
                <ol className="space-y-3 text-sm text-slate-700 list-decimal list-inside">
                  <li>
                    <a
                      href={manifestHref}
                      onClick={() => Telemetry.configuratorInstallAttempted({ method: 'sideload' })}
                      className="text-brand-orange font-medium underline"
                    >
                      Download your manifest file
                    </a>
                  </li>
                  <li>In Word, go to <strong>Insert → Add-ins → Upload My Add-in → Browse</strong></li>
                  <li>Select the downloaded <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">manifest.xml</code> file.</li>
                  <li>The Chrona tab appears in the ribbon.</li>
                </ol>
              ) : (
                <ol className="space-y-3 text-sm text-slate-700 list-decimal list-inside">
                  <li>
                    Download the manifest file from your IT admin, or{' '}
                    <Link to="/configure" className="text-brand-orange underline">
                      build your ribbon
                    </Link>{' '}
                    to get a personal manifest.
                  </li>
                  <li>In Word, go to <strong>Insert → Add-ins → Upload My Add-in → Browse</strong></li>
                  <li>Select the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">manifest.xml</code> file.</li>
                  <li>The Chrona tab appears in the ribbon.</li>
                </ol>
              )}
            </div>
          )}
        </section>

        {/* ── Troubleshooting (collapsible) ── */}
        <section className="border border-slate-200 bg-white rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowTroubleshooting((s) => !s)}
            className="w-full flex items-center justify-between px-5 py-4 text-sm font-medium hover:bg-slate-50 text-left"
          >
            <span>Troubleshooting</span>
            {showTroubleshooting ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showTroubleshooting && (
            <div className="px-5 pb-5 pt-1 border-t border-slate-100 space-y-4 text-sm text-slate-700">
              <div className="flex items-start gap-3">
                <WifiOff size={16} className="mt-0.5 shrink-0 text-slate-400" />
                <div>
                  <p className="font-medium">Chrona tab doesn&apos;t appear</p>
                  <p className="text-slate-500">Restart Word. For admin-deployed add-ins, allow 12–24 hours for the add-in to propagate.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MonitorCheck size={16} className="mt-0.5 shrink-0 text-slate-400" />
                <div>
                  <p className="font-medium">Taskpane is blank</p>
                  <p className="text-slate-500">Check your internet connection. The add-in requires HTTPS access to plugin.chronabio.ai.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-slate-400" />
                <div>
                  <p className="font-medium">Can&apos;t sign in</p>
                  <p className="text-slate-500">Make sure you&apos;re using the same email you signed up with.</p>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Footer CTA ── */}
        <p className="text-sm text-center text-slate-500">
          Ready to customize your ribbon?{' '}
          <Link to="/configure" className="text-brand-orange font-medium hover:underline">
            Configure →
          </Link>
        </p>
      </main>
      <TermsPrivacyFooter />
    </div>
  )
}
